// tabs/ability-effect-button.ts — the roll-damage "effect" button shown in the charges column
// of every ability row (creatures AND PCs). Client port of render_ability_effect_button()
// (includes/functions/janus/core/ability_effect_speed_renderers.php:38-117) + its calculators
// calculate_damage_dice / calculate_damage_mod / generate_damage_type_select.
//
// Summon/shapeshift selects from the PHP version are NOT emitted here — the shared name cell
// already renders them via choiceSelect (ability-row.ts), so emitting them here would duplicate.

import type { CachedCaster } from '../types';
import { k5Environment } from '../k5-runtime';
import { calculateDamageDice, calculateDamageMod } from '../../janus-engine/port/k5-render/ability_effect_speed_renderers';
import type { Row } from '../../janus-engine/port/k5-render/support';
import {
    iconPh,
    uuidOf,
} from './ability-row';

// RangeType 'self'.
const RANGE_SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';

// Roll-type variable_names that route the button to useAbilityVariable1('…','damage_only')
// instead of rollDice(...) — janus_hostile_paoe_roll_type_ids().
const HOSTILE_PAOE_ROLL_VNS = [
    'damage_roll', 'contesting_roll', 'damage_over_time_roll', 'weapon_melee_attack_roll',
    'weapon_ranged_attack_roll', 'spell_melee_attack_roll', 'spell_ranged_attack_roll',
    'extra_damage_taken_roll', 'mana_drain_roll',
];

// Heal-family roll types (is_ability_roll_type_healing_or_regeneration()).
const HEAL_FAMILY_ROLL_VNS = ['healing_roll', 'regeneration_roll', 'temporary_vitality_roll', 'temporary_vitality_regeneration_roll'];

/** Whether a row's red dice button USES the ability instead of posting an example roll (owner 2026-09-16/17). A sample
 *  roll of a heal logged "Healing 18" through roll_inc.php while nothing healed and nothing was spent, and players read
 *  it as the heal failing. Uses: every heal-family roll (any range — a targeted heal arms targeting like its row
 *  button), and any non-hostile Self-range roll. Hostile rolls keep their own damage_only path. */
export function effectButtonUsesAbility(ab: unknown, rollType: string, isHealing = false): boolean {
    const hostile = new Set(HOSTILE_PAOE_ROLL_VNS.map(rollTypeId).filter(Boolean));
    if (hostile.has(rollType)) return false;
    if (isHealing || HEAL_FAMILY_ROLL_VNS.map(rollTypeId).filter(Boolean).includes(rollType)) return true;
    return String(ftLayerValue(ab, 'range') ?? '').toUpperCase() === RANGE_SELF;
}

/** Inline onclick that clicks the row's own ability button, so the cast keeps that button's exact dispatch (charge /
 *  nested-source markers, targeting) and its disabled state (no charges, not your sheet — a disabled button carries no
 *  action, so the click is a no-op). A row with no ability button (a static statblock) runs `fallback`. */
export function useRowAbilitySnippet(id: string, fallback: string): string {
    return `(function(b){var r=b.closest('tr');var p=r&&r.querySelector('[data-ability_id=\\'${escapeHtml(id)}\\']');if(p){p.click();return;}${fallback}})(this)`;
}

function rollTypeCatalog(): Record<string, { item_id?: string; item_name?: string }> {
    const c = window.ROLLTYPE_CATALOG;
    return (c && typeof c === 'object') ? c : {};
}
function rollTypeId(variableName: string): string {
    return String(rollTypeCatalog()[variableName]?.item_id || '');
}
function calcDamageDice(dice: unknown, ab: unknown, caster: CachedCaster): unknown {
    return calculateDamageDice(dice, ab as Row, caster, k5Environment());
}
function calcDamageMod(ab: unknown, caster: CachedCaster): unknown {
    return calculateDamageMod(ab as Row, caster, k5Environment());
}

function damageTypeName(id: string): string {
    const nm = (typeof window.ftItemNameById === 'function') ? String(window.ftItemNameById(id) ?? '') : '';
    return nm !== '' ? nm : 'Damage Type';
}

/**
 * The roll-damage button for an ability row (or '' when the ability has no `die`/damage).
 * Port of render_ability_effect_button minus the summon/shapeshift selects.
 */
// `domScope` namespaces ONLY the multi-damage-type <select>'s DOM id (not the ability `id` used for
// the roll/use snippet), so the SAME ability can render this button in two sections without colliding
// on `input_<id>_user_choice_1_damage_type`. Default '' preserves the original id for the ability rows.
// The Natural Weapons table passes a scope because a multi-type natural weapon can also appear under
// Attack Abilities (duplicate DOM id would otherwise cross-wire the two choosers).
export function abilityEffectButton(caster: CachedCaster, ab: unknown, id: string, ro: boolean, domScope = ''): string {
    if (!ftLayerHas(ab, 'die')) return '';

    const die = ftLayerValue(ab, 'die');
    const dice = calcDamageDice(ftLayerValue(ab, 'dice'), ab, caster);
    const mod = calcDamageMod(ab, caster);
    const rollType = String(ftLayerValue(ab, 'roll_type') || rollTypeId('damage_roll'));
    const hostile = new Set(HOSTILE_PAOE_ROLL_VNS.map(rollTypeId).filter(Boolean));

    const dtIds = ftLayerEntries(ab, 'damage_type').map(uuidOf).filter((x: string) => x !== '');
    const primaryDt = dtIds[0] || '';
    // >1 damage_type = a USER-CHOICE type (PHP generate_damage_type_select parity, restored
    // 2026-07-01 — e.g. the Spellblade's Elemental Attack: frost/fire/lightning/storm). The
    // icon-only TomSelect chooser renders NEXT to the one red button (canonical
    // input_<id>_user_choice_1_damage_type — the SAME id the ability's use button reads), and
    // the rolls below honour the pick at click time. A single type keeps its icon inline.
    const multiDt = dtIds.length > 1;
    const dmgSelId = `input_${domScope ? domScope + '_' : ''}${id}_user_choice_1_damage_type`;
    // Inline JS expression: the picked type at CLICK time, else the primary.
    const pickedDtExpr = `((document.getElementById('${escapeHtml(dmgSelId)}')||{}).value||'${escapeHtml(primaryDt)}')`;

    // FT3D hostile chips arm the shared target draft as a damage-only action. The browser applies
    // damage and mitigation without casting the ability. A static sheet retains its example dice
    // roll; the shared actor permission gate governs encounter actions, including inspected sheets.
    // Multi-type chips pass their selected type in the second variable slot.
    const dtArg = multiDt ? pickedDtExpr : `'${escapeHtml(primaryDt)}'`;
    const exampleRoll = `rollDice('${escapeHtml(rollType)}', ${dtArg}, '${escapeHtml(id)}', '', '${escapeHtml(String(dice))}', '${escapeHtml(String(die))}', '${escapeHtml(String(mod ?? 0))}', '0');`;
    const damageOnly = multiDt
        ? `useAbilityVariable2('${escapeHtml(id)}','damage_only',${pickedDtExpr})`
        : `useAbilityVariable1('${escapeHtml(id)}','damage_only')`;
    const ec = caster.encounter_creatures;
    const casterId = String((ec && !Array.isArray(ec) ? ec.encounter_creature_id : '') ?? ftItemsCol(caster, 'item_id') ?? '');
    const targetedDamage = `window.ftDispatchAbilityVariantClick&&window.ftDispatchAbilityVariantClick('${escapeHtml(casterId)}','${escapeHtml(id)}',()=>{${damageOnly}},true)`;
    const selfUse = effectButtonUsesAbility(ab, rollType);
    const useRowAbility = useRowAbilitySnippet(id, exampleRoll);
    const onclick = hostile.has(rollType)
        // Ability-first in FT3D: choose the damage action, then pick/confirm its targets. Static sheets
        // with no encounter caster retain the harmless example-roll fallback.
        ? `(${targetedDamage})?void 0:${exampleRoll}`
        : selfUse ? useRowAbility : exampleRoll;

    let out = `<button type="button" class="btn btn-sm btn-danger border border-dark"${selfUse ? ` title="Use ${escapeHtml(String(ftItemsCol(ab, 'item_name') ?? 'ability'))}"` : ''} onclick="${onclick}">`;
    out += escapeHtml(`${dice}d${die}`);
    if (mod !== null && mod !== undefined && mod !== '' && mod !== 0 && mod !== '0') {
        out += ' + ' + escapeHtml(String(mod));
    }

    // Damage-type icon inside the button (single type only — a multi-type chooser carries the
    // icons in its options, PHP parity); else the healing / temp-vitality roll cue.
    if (dtIds.length === 1) {
        out += ' ' + iconPh(primaryDt);
    } else if (rollType === rollTypeId('healing_roll')) {
        out += ' ' + iconPh('healing');
    } else if (rollType === rollTypeId('temporary_vitality_roll') || rollType === rollTypeId('temporary_vitality_regeneration_roll')) {
        out += ' ' + iconPh('v_temp');
    }

    out += '</button>';

    if (multiDt) {
        // Icon-only TomSelect chooser (mirror of castable-spells damageTypeSelect / PHP
        // generate_damage_type_select). iconMap (direct <img>) — TomSelect clones the data-icon
        // HTML into its dropdown, so an iconPh placeholder would never hydrate there.
        let sel = `<select class="ts-select ts-auto-width icon-only-damage-select" id="${escapeHtml(dmgSelId)}" title="Choose Damage Type"${ro ? ' disabled' : ''}>`;
        for (const dt of dtIds) {
            const icon = iconMap(dt, 25, 25, false);
            sel += `<option value="${escapeHtml(dt)}" data-icon="${escapeHtml(icon)}" title="${escapeHtml(damageTypeName(dt))}"></option>`;
        }
        sel += '</select>';
        // ONE attached unit — the dice button and the type select share squared edges
        // (.ft-ability-effect-unit, 95-pc-sheet.css) so the pair reads as a single damage chip
        // and right-aligns as one block with the plain single-type buttons across rows.
        out = `<span class="ft-ability-effect-unit">${out}${sel}</span>`;
    }
    return out;
}
